import type { ReceiptFields } from './eusign';

/**
 * Друкований бланк квитанції ДПС (форма J1499202) — один-в-один з офіційним
 * виглядом «Квитанція № 1/2»: таблиця з рамками, поля з XML квитанції.
 * Використовується у попапі Кабінету ДПС; друк/збереження у PDF — через
 * `window.print()` (бланк лишається, решта сторінки ховається у @media print).
 */

const box: React.CSSProperties = {
  border: '1px solid #000', borderCollapse: 'collapse', width: '100%',
  fontFamily: 'Times, "Times New Roman", serif', fontSize: '10pt',
  color: '#000', background: '#fff', tableLayout: 'fixed',
};
const td: React.CSSProperties = {
  border: '1px solid #000', padding: '3pt 6pt', verticalAlign: 'top',
  wordBreak: 'break-word',
};
const lbl: React.CSSProperties = { ...td, width: '32%', fontWeight: 400 };
const val: React.CSSProperties = { ...td, fontWeight: 700 };
const hint: React.CSSProperties = {
  fontSize: '7pt', fontWeight: 400, color: '#000', textAlign: 'center',
  display: 'block', marginTop: '1pt',
};

function Hint({ children }: { children: React.ReactNode }) {
  return <span style={hint}>{children}</span>;
}

export default function DpsReceiptBlank({ f }: { f: ReceiptFields }) {
  return (
    <div className="dps-receipt-blank" style={{
      fontFamily: 'Times, serif', color: '#000', background: '#fff',
      maxWidth: '190mm', margin: '0 auto',
    }}>
      <div style={{ fontSize: '8pt', marginBottom: '2pt' }}>J1499202</div>
      <table style={box}>
        <tbody>
          <tr>
            <td colSpan={2} style={{ ...td, textAlign: 'center', fontWeight: 700,
              fontSize: '12pt' }}>
              Квитанція № {f.num}
            </td>
          </tr>

          <tr>
            <td style={lbl}>Платник податків:</td>
            <td style={val}>
              {f.tin}
              <Hint>(код згідно з ЄДРПОУ або РНОКПП або серія та номер паспорта)</Hint>
              <div style={{ marginTop: '3pt' }}>{f.name}</div>
              <Hint>(найменування або прізвище, ім'я, по батькові)</Hint>
              {f.email && <>
                <div style={{ marginTop: '3pt', fontWeight: 400 }}>{f.email}</div>
                <Hint>(адреса електронної пошти (E-mail))</Hint>
              </>}
            </td>
          </tr>

          <tr>
            <td style={lbl}>Документ:</td>
            <td style={val}>
              {f.docKod}
              <Hint>(код форми документу)</Hint>
              <div style={{ marginTop: '3pt' }}>{f.docName}</div>
              <Hint>(назва документу звітності)</Hint>
              <div style={{ marginTop: '3pt', wordBreak: 'break-all' }}>{f.fileName}</div>
              <Hint>(ім'я файлу)</Hint>
              {f.docStan && <>
                <div style={{ marginTop: '3pt' }}>{f.docStan}</div>
                <Hint>(стан документа)</Hint>
              </>}
              <div style={{ marginTop: '3pt' }}>
                {f.period} {f.year && <span style={{ marginLeft: '1em' }}>{f.year}</span>}
              </div>
              <Hint>(назва звітного періоду)&nbsp;&nbsp;&nbsp;(звітний рік)</Hint>
              {f.term && <>
                <div style={{ marginTop: '3pt', fontWeight: 400 }}>{f.term}</div>
                <Hint>(граничний термін подання)</Hint>
              </>}
            </td>
          </tr>

          <tr>
            <td style={lbl}>Підписи документа:</td>
            <td style={val}>
              {(f.bos || f.bosCode) && <>
                <div style={{ fontWeight: 400, fontSize: '8pt' }}>
                  Керівник або фізична особа – платник податку, або співробітник
                  з правом підпису:
                </div>
                <div style={{ marginTop: '2pt' }}>
                  {f.bosCode}&nbsp;&nbsp;{f.bos}
                </div>
                <Hint>(РНОКПП або серія та номер паспорта)&nbsp;&nbsp;(прізвище, ім'я, по батькові)</Hint>
              </>}
              {(f.buh || f.buhCode) && <>
                <div style={{ fontWeight: 400, fontSize: '8pt', marginTop: '3pt' }}>
                  бухгалтер:
                </div>
                <div style={{ marginTop: '2pt' }}>
                  {f.buhCode}&nbsp;&nbsp;{f.buh}
                </div>
                <Hint>(РНОКПП або серія та номер паспорта)&nbsp;&nbsp;(прізвище, ім'я, по батькові)</Hint>
              </>}
            </td>
          </tr>

          <tr>
            <td style={lbl}>Результат обробки:</td>
            <td style={val}>
              {(f.orgCode || f.stiName) && <>
                <div style={{ fontWeight: 400 }}>
                  Документ доставлено до <b>{f.orgCode}</b>
                </div>
                <Hint>(код контролюючого органу)</Hint>
                {f.stiName && <>
                  <div style={{ marginTop: '2pt' }}>{f.stiName}</div>
                  <Hint>(назва контролюючого органу)</Hint>
                </>}
              </>}
              {f.result && (
                <div style={{ marginTop: '4pt', whiteSpace: 'pre-wrap' }}>{f.result}</div>
              )}
              {(f.date || f.time) && <>
                <div style={{ marginTop: '3pt', fontWeight: 400 }}>
                  {f.date} &nbsp;&nbsp; {f.time}
                </div>
                <Hint>(дата)&nbsp;&nbsp;&nbsp;(час)</Hint>
              </>}
              {f.regNum && (
                <div style={{ marginTop: '3pt' }}>
                  <span style={{ fontWeight: 400 }}>Реєстраційний №&nbsp;&nbsp;</span>{f.regNum}
                </div>
              )}
            </td>
          </tr>

          {f.text && (
            <tr>
              <td colSpan={2} style={{ ...td, fontWeight: 400, whiteSpace: 'pre-wrap',
                fontSize: '9pt' }}>
                {f.text}
              </td>
            </tr>
          )}

          {f.sender && (
            <tr>
              <td style={lbl}>Відправник:</td>
              <td style={val}>
                {f.sender}
                <Hint>(інформація про відправника)</Hint>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
